import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Loader2, FolderTree, Folder, FileText, Download, Building2, MapPin, Layers } from "lucide-react";
import { toast } from "sonner";

const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }) : "—";

export default function PortalDocumentsTab({ clientId }) {
  const [tree, setTree] = useState(null);
  const [node, setNode] = useState(null);       // {level, id, name}
  const [docs, setDocs] = useState(null);
  const [open, setOpen] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/documents/tree");
        const mine = (data.clients || []).find((c) => c.id === clientId) || data.clients?.[0] || false;
        setTree(mine);
        if (mine) selectNode({ level: "client", id: mine.id, name: mine.name });
      } catch (e) { toast.error(e.response?.data?.detail || "Documents unavailable"); setTree(false); }
    })();
    // eslint-disable-next-line
  }, [clientId]);

  const selectNode = async (n) => {
    setNode(n); setDocs(null);
    try {
      const params = { client_id: clientId };
      if (n.level === "site") params.site_id = n.id;
      if (n.level === "building") params.building_id = n.id;
      if (n.level === "area") params.area_id = n.id;
      const { data } = await api.get("/documents", { params });
      setDocs(data);
    } catch (e) { toast.error("Could not load documents"); setDocs([]); }
  };

  if (tree === null) {
    return <div className="flex items-center gap-2 text-sm text-slate-500 py-10 justify-center">
      <Loader2 className="w-4 h-4 animate-spin" /> Loading documents…</div>;
  }
  if (!tree) {
    return <div className="text-center text-sm text-slate-400 py-12" data-testid="portal-docs-empty">
      No document library has been set up for your organisation yet.</div>;
  }

  const NodeBtn = ({ n, icon: Icon, count, depth }) => (
    <button onClick={() => selectNode(n)}
            data-testid={`portal-doc-node-${n.id}`}
            className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-left text-xs transition
              ${node?.id === n.id ? "bg-gbe text-white" : "text-slate-700 hover:bg-slate-100"}`}
            style={{ paddingLeft: `${8 + depth * 14}px` }}>
      <Icon className="w-3.5 h-3.5 shrink-0 opacity-70" />
      <span className="flex-1 truncate">{n.name}</span>
      {count > 0 && <span className={`text-[10px] tabular-nums ${node?.id === n.id ? "text-white/80" : "text-slate-400"}`}>{count}</span>}
    </button>
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="portal-docs-tab">
      {/* Tree */}
      <div className="rounded-lg border border-slate-200 bg-white p-3 max-h-[540px] overflow-y-auto">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-slate-500 mb-2 px-1">
          <FolderTree className="w-3.5 h-3.5" /> Your document library
        </div>
        <NodeBtn n={{ level: "client", id: tree.id, name: tree.name }} icon={Folder} count={tree.doc_count} depth={0} />
        {(tree.sites || []).map((s) => (
          <div key={s.id}>
            <div className="flex items-center">
              <button onClick={() => setOpen({ ...open, [s.id]: !open[s.id] })}
                      className="text-slate-400 text-[10px] w-4">{(s.buildings || []).length > 0 ? (open[s.id] ? "▾" : "▸") : ""}</button>
              <div className="flex-1"><NodeBtn n={{ level: "site", id: s.id, name: s.name }} icon={MapPin} count={s.doc_count} depth={0} /></div>
            </div>
            {open[s.id] && (s.buildings || []).map((b) => (
              <div key={b.id}>
                <NodeBtn n={{ level: "building", id: b.id, name: b.name }} icon={Building2} count={b.doc_count} depth={1} />
                {(b.areas || []).map((a) => (
                  <NodeBtn key={a.id} n={{ level: "area", id: a.id, name: a.name }} icon={Layers} count={a.doc_count} depth={2} />
                ))}
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* Documents */}
      <div className="lg:col-span-2 rounded-lg border border-slate-200 bg-white overflow-hidden">
        <div className="px-4 py-2.5 border-b border-slate-200 flex items-center justify-between">
          <span className="text-sm font-semibold text-slate-800 truncate">{node?.name || "Documents"}</span>
          <span className="text-[11px] text-slate-500">{docs?.length ?? "…"} document(s) · read-only</span>
        </div>
        {!docs ? (
          <div className="flex items-center gap-2 text-sm text-slate-500 py-10 justify-center">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : docs.length === 0 ? (
          <div className="text-center text-xs text-slate-400 py-12" data-testid="portal-docs-none">No documents in this location.</div>
        ) : (
          <table className="w-full text-xs">
            <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase text-slate-500">
              <tr>
                <th className="text-left px-3 py-2">Document</th>
                <th className="text-left px-3 py-2">Type</th>
                <th className="text-left px-3 py-2">Uploaded</th>
                <th className="text-left px-3 py-2">Expiry</th>
                <th className="w-12"></th>
              </tr>
            </thead>
            <tbody data-testid="portal-docs-list">
              {docs.map((d) => (
                <tr key={d.id} className="border-b border-slate-100 hover:bg-slate-50/50">
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <FileText className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                      <div>
                        <div className="text-slate-800">{d.title || d.file_name}</div>
                        {d.description && <div className="text-[10px] text-slate-400 truncate max-w-[280px]">{d.description}</div>}
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2 text-slate-500 capitalize">{(d.document_type || "").replace("_", " ")}</td>
                  <td className="px-3 py-2 text-slate-500 whitespace-nowrap">{fmtDate(d.uploaded_at)}</td>
                  <td className={`px-3 py-2 whitespace-nowrap ${d.expiry_date && d.expiry_date < new Date().toISOString().slice(0, 10) ? "text-rose-600 font-medium" : "text-slate-500"}`}>
                    {fmtDate(d.expiry_date)}
                  </td>
                  <td className="px-3 py-2">
                    <a href={d.storage_url} target="_blank" rel="noopener noreferrer"
                       data-testid={`portal-doc-download-${d.id}`}
                       className="text-slate-400 hover:text-gbe" title="Download">
                      <Download className="w-4 h-4" />
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
